import { DatePipe } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, FormArray, FormArrayName } from "@angular/forms";
import { FlexDirective, LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { FileSizePipe, LabeledFieldComponent, TruncateTextComponent } from "@tylertech/common-components-module";
import { ForgeCardModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { DocumentSearchCriteria } from "@tylertech/tyler-api-types";
import { AttachmentModel } from "../../interfaces";
import { AttachmentService } from "../../services";

@Component({
    selector: "attachment",
    templateUrl: "./attachment.tmpl.html",
    imports: [
        CommandBarComponent,
        DatePipe,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        FileSizePipe,
        FlexFillDirective,
        ForgeCardModule,
        ForgeTooltipModule,
        LabeledFieldComponent,
        TruncateTextComponent,
    ]
})
export class AttachmentComponent
    implements OnInit
{
    private readonly formArrayNameDirective = inject(FormArrayName);
    private readonly $attachmentService = inject(AttachmentService);

    @Input() readonly canEdit: boolean;
    @Input() readonly entityType: string;
    @Input() readonly index: number;

    @Output() readonly delete = new EventEmitter<AttachmentModel>();

    readonly commands: ReadonlyArray<Command>;

    constructor()
    {
        this.commands = [
            new IconCommand({
                icon: "file_download",
                tooltip: "Download",
                action: () => this.download(),
            }),
            new IconCommand({
                icon: "delete",
                tooltip: "Delete",
                action: () => this.delete.emit(this.model),
                getVisible: () => this.canEdit,
            }),
        ];
    }

    private get formArray(): FormArray { return this.formArrayNameDirective.control; }
    private get formControl(): AbstractControl<AttachmentModel> { return this.formArray.get([this.index]); }
    private get model(): AttachmentModel { return this.formControl?.value; }

    get maxFileSize(): number
    {
        return this.$attachmentService.MaxFileSize;
    }

    get type(): string { return this.parseFileType(); }

    get addedDate(): Date { return this.model.AddedDate; }
    get addedBy(): string { return this.model.AddedBy; }
    get fileName(): string { return this.model.Filename; }
    get fileSize(): number { return this.model.File.size; }

    get invalid(): boolean { return this.formControl.invalid; }

    get supportedFileTypes(): string
    {
        return this.$attachmentService.getSupportedFileTypes().join(", ");
    }

    ngOnInit(): void
    {
        if (!this.formControl)
        {
            throw new Error(`AttachmentComponent: Must be nested in FormArray`);
        }
    }

    private download(): void
    {
        const criteria: DocumentSearchCriteria = {
            EntityId: this.model.Id,
            EntityType: this.entityType,
        };
        this.$attachmentService.Download(criteria, this.model);
    }

    private parseFileType(): string
    {
        const nameParts = this.model.Filename.split(".");
        const fileExtension = nameParts.length > 1
            ? nameParts.pop().toLowerCase()
            : undefined;

        const fileType = this.$attachmentService.isSupported(fileExtension)
            ? this.$attachmentService.getFileTypeName(fileExtension)
            : "Not Specified";
        return fileType;
    }

    hasError(key: string): boolean
    {
        return this.formControl.hasError(key);
    }
}
